import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  computed,
  inject,
  Injectable,
  signal,
  WritableSignal,
} from '@angular/core';
import { emailRegex } from '@app/utils';
import { environment } from '@environments/_current/environment';
import {
  catchError,
  delay,
  firstValueFrom,
  map,
  Observable,
  of,
  tap,
  throwError,
} from 'rxjs';
import { AdminTenantService } from '../admin-tenant/admin-tenant.service';
import { Logger } from '../logger/logger.service';
import {
  BatchResult,
  BatchState,
  EmailResult,
  EmailStatus,
} from './admin-eligibility-file-import.model';

export interface FileDetailError {
  type: 'file' | 'value';
  message: string;
}

export interface FileDetail {
  name: string;
  size: number;
  type: string;
  emailsParsed: number;
  valuesIgnored: number;
  errors: FileDetailError[];
}

interface ParseValues {
  emailsParsed: string[];
  valuesIgnored: string[];
}

export const MAX_ALLOWED_SIZE = 1_572_864; // This is in bytes (1.5mb)
const MAX_ALLOWED_NUM_OF_EMAILS = 5_000;
const SUPPORTED_FILES = ['text/csv', 'text/txt'];
const EMAIL_BATCH_SIZE = 50;
const BATCH_PROCESSING_DELAY_MS = 500;
const REMOVED_USERS_CACHE_KEY = 'eligibility_file_removed_users';

export type OperationMode = 'invite' | 'remove';

@Injectable({
  providedIn: 'root',
})
export class AdminEligibilityFileImportService {
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly logger = inject(Logger);
  private readonly http = inject(HttpClient);
  private readonly batchSize = EMAIL_BATCH_SIZE;
  operationMode = signal<OperationMode>('invite');
  processedFiles = signal<FileDetail[]>([]);
  totalFileSizeUploaded = signal(0);
  totalEmailsParsed = signal(0);
  totalValuesIgnored = signal(0);
  duplicateEmails = signal<string[]>([]);
  emailsToSubmit = signal<Set<string>>(new Set());
  emailsToSubmitBatches = signal<string[][]>([]);
  unsuccessfulEmailsList = signal<string[]>([]);
  unprocessedEmailsList = signal<string[]>([]);
  public state: WritableSignal<BatchState> = signal({
    isProcessing: false,
    totalEmails: 0,
    batchSize: this.batchSize,
    batches: [],
    globalStatus: 'ready',
  });
  public processingCount = computed(
    () => this.state().batches.filter((b) => b.status === 'succeeded').length
  );
  public totalBatches = computed(() => this.state().batches.length);
  public failedEmails = computed(() =>
    this.state().batches.flatMap((b) =>
      b.emails.filter((e) => e.status === 'failed')
    )
  );

  /**
   * Computed value that combines unsuccessful and unprocessed emails with their statuses
   * for CSV export: [{ email: string, status: 'unsuccessful' | 'unprocessed' }]
   */
  public emailsForCSVExport = computed(() => {
    const unsuccessful = this.unsuccessfulEmailsList().map((email) => ({
      email,
      status: 'unsuccessful' as const,
    }));
    const unprocessed = this.unprocessedEmailsList().map((email) => ({
      email,
      status: 'unprocessed' as const,
    }));
    return [...unsuccessful, ...unprocessed];
  });

  clearProcessedFiles() {
    this.processedFiles.set([]);
    this.totalFileSizeUploaded.set(0);
    this.totalEmailsParsed.set(0);
    this.totalValuesIgnored.set(0);
    this.emailsToSubmit.set(new Set());
    this.emailsToSubmitBatches.set([]);
    this.duplicateEmails.set([]);
    this.unsuccessfulEmailsList.set([]);
    this.unprocessedEmailsList.set([]);
    this.operationMode.set('invite');
  }

  /**
   * Get the set of emails that have been successfully removed (from cache)
   */
  private getRemovedUsersCache(): Set<string> {
    try {
      const cached = localStorage.getItem(REMOVED_USERS_CACHE_KEY);
      if (cached) {
        const emails = JSON.parse(cached) as string[];
        return new Set(emails);
      }
    } catch (error) {
      console.error('Error reading removed users cache:', error);
    }
    return new Set<string>();
  }

  /**
   * Add an email to the removed users cache
   */
  private addToRemovedUsersCache(email: string): void {
    try {
      const cache = this.getRemovedUsersCache();
      cache.add(email);
      localStorage.setItem(
        REMOVED_USERS_CACHE_KEY,
        JSON.stringify(Array.from(cache))
      );
    } catch (error) {
      console.error('Error updating removed users cache:', error);
    }
  }

  /**
   * Downloads failed and unprocessed emails as a CSV file with email and status columns
   */
  downloadFailedEmailsCSV(): void {
    const emailsForExport = this.emailsForCSVExport();

    if (emailsForExport.length === 0) {
      console.warn('No failed or unprocessed emails to download');
      return;
    }

    const csvRows: string[] = ['email,status'];
    emailsForExport.forEach((item) => {
      csvRows.push(`${item.email},${item.status}`);
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const localDate = `${year}-${month}-${day}`;
    const mode = this.operationMode();
    const filename =
      mode === 'remove'
        ? `failed-removals-${localDate}.csv`
        : `failed-invites-${localDate}.csv`;
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  public prepareBatches(): void {
    const allEmails = Array.from(this.emailsToSubmit());
    const mode = this.operationMode();
    const removedUsersCache =
      mode === 'remove' ? this.getRemovedUsersCache() : new Set<string>();

    // Filter out already-removed emails and mark them
    const emailsToProcess: EmailResult[] = [];
    const alreadyRemovedEmails: EmailResult[] = [];

    allEmails.forEach((email) => {
      if (mode === 'remove' && removedUsersCache.has(email)) {
        // Email was already removed - mark it as failed with specific message
        alreadyRemovedEmails.push({
          email,
          status: 'failed',
          message: 'User not in tenant',
        });
      } else {
        // Email needs to be processed
        emailsToProcess.push({ email, status: 'pending' });
      }
    });

    // Create batches from emails that need processing
    const batches: BatchResult[] = [];
    for (let i = 0; i < emailsToProcess.length; i += this.batchSize) {
      const emailSlice = emailsToProcess.slice(i, i + this.batchSize);
      batches.push({
        batchId: Math.floor(i / this.batchSize) + 1,
        emails: emailSlice,
        status: 'pending',
      });
    }

    // If there are already-removed emails, add them to batches
    if (alreadyRemovedEmails.length > 0) {
      if (batches.length > 0) {
        // Add already-removed emails to the first batch so they show up in results
        batches[0].emails = [...alreadyRemovedEmails, ...batches[0].emails];
      } else {
        // If no batches but we have already-removed emails, create a batch for them
        batches.push({
          batchId: 1,
          emails: alreadyRemovedEmails,
          status: 'failed',
        });
      }
    }

    this.state.set({
      isProcessing: false,
      totalEmails: allEmails.length,
      batchSize: this.batchSize,
      batches: batches,
      globalStatus: 'ready',
    });
  }

  /**
   * 2. The core logic to run all batches sequentially (Blocking v1).
   */
  public async runAllBatches(): Promise<void> {
    if (this.state().isProcessing) return;

    this.state.update((s) => ({
      ...s,
      isProcessing: true,
      globalStatus: 'running',
    }));

    const batches = [...this.state().batches];
    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      if (this.state().globalStatus === 'stopped') {
        break; // Stop execution if a global stop signal was set
      }

      // Check if batch only contains already-removed emails (pre-marked as failed)
      const hasEmailsToProcess = batch.emails.some(
        (e) => e.status === 'pending'
      );

      if (!hasEmailsToProcess) {
        // Batch only has already-removed emails, mark as completed
        this.updateBatchStatus(batch.batchId, 'failed');
        continue;
      }

      this.updateBatchStatus(batch.batchId, 'running');

      try {
        await firstValueFrom(this.executeBatchWithRetry(batch));

        if (i < batches.length - 1) {
          await firstValueFrom(of(null).pipe(delay(BATCH_PROCESSING_DELAY_MS)));
        }
      } catch (error) {
        console.warn('Error happening when running all batches', error);
        break;
      }
    }

    this.captureUnprocessedEmails();

    if (this.state().globalStatus === 'running') {
      this.state.update((s) => ({
        ...s,
        isProcessing: false,
        globalStatus: 'completed',
      }));
    } else {
      this.state.update((s) => ({ ...s, isProcessing: false }));
    }
  }

  /**
   * 3. Executes a single batch and handles the retry logic (Attempt 1 + 1 Retry).
   */
  public executeBatchWithRetry(batch: BatchResult): Observable<BatchResult> {
    return this.sendBatch(batch).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status >= 400) {
          this.updateBatchStatus(batch.batchId, 'retrying');

          // Attempt 2 (Retry)
          return this.sendBatch(batch).pipe(
            catchError((retryError: HttpErrorResponse) => {
              this.handleFailure(batch, retryError);
              return throwError(
                () => new Error('Global stop: Batch failed after two attempts.')
              );
            })
          );
        }
        // If it's a non-retriable error (e.g., network error, client-side), stop globally.
        this.handleFailure(batch, error);
        return throwError(
          () => new Error('Global stop: Unhandled error during batch send.')
        );
      })
    );
  }

  /**
   * Helper to send data to the backend endpoint.
   */
  private sendBatch(batch: BatchResult): Observable<BatchResult> {
    const mode = this.operationMode();
    const tenantId = this.adminTenantService.getTenantId();

    if (mode === 'remove') {
      // Remove users endpoint - use default tenant ID from environment
      const enterpriseUserGroupId = environment.defaultTenantId;
      const apiUrl = `${environment.aaptivServiceUrl}/api/v2/enterpriseUserGroup/${enterpriseUserGroupId}/reassign/bulk`;
      // Only send emails that are pending (filter out already-removed emails)
      const usersToSend = batch.emails
        .filter((e) => e.status === 'pending')
        .map((e) => e.email);

      // If no emails to send (all were already removed), skip API call
      if (usersToSend.length === 0) {
        // Update batch results with empty response (already-removed emails are already marked)
        this.updateBatchResults(batch.batchId, {
          results: { successful: [], failed: [] },
        });
        return of(batch);
      }

      return this.http.patch<any>(apiUrl, { users: usersToSend }).pipe(
        tap((response) => {
          this.updateBatchResults(batch.batchId, response);
        }),
        map(() => batch)
      );
    } else {
      // Invite users endpoint
      const apiUrl = `${environment.aaptivServiceUrl}/api/v2/enterpriseTenants/${tenantId}/invite`;
      // Only send emails that are pending
      const usersToSend = batch.emails
        .filter((e) => e.status === 'pending')
        .map((e) => ({ email: e.email }));

      return this.http.post<any>(apiUrl, { users: usersToSend }).pipe(
        tap((response) => {
          const responseArray = Array.isArray(response) ? response : [];
          this.updateBatchResults(batch.batchId, responseArray);
        }),
        map(() => batch)
      );
    }
  }

  /**
   * Helper to update the state signals after a successful HTTP call.
   */
  private updateBatchResults(batchId: number, response: any): void {
    try {
      this.state.update((s) => {
        const batchIndex = s.batches.findIndex((b) => b.batchId === batchId);
        if (batchIndex > -1) {
          const batch = s.batches[batchIndex];

          const mode = this.operationMode();
          const responseMap = new Map<
            string,
            { success: boolean; error?: string }
          >();

          if (mode === 'remove') {
            // For remove, response has structure: { results: { successful: [...], failed: [...] }, summary: {...} }
            if (response?.results) {
              // Process successful removals
              if (Array.isArray(response.results.successful)) {
                response.results.successful.forEach((item: any) => {
                  if (item?.email) {
                    responseMap.set(item.email, { success: true });
                  }
                });
              }
              // Process failed removals with error messages
              if (Array.isArray(response.results.failed)) {
                response.results.failed.forEach((item: any) => {
                  if (item?.email) {
                    responseMap.set(item.email, {
                      success: false,
                      error: item.error || 'Failed to remove.',
                    });
                  }
                });
              }
            } else if (Array.isArray(response)) {
              // Fallback: handle array response format
              response.forEach((item: any) => {
                if (typeof item === 'string') {
                  responseMap.set(item, { success: true });
                } else if (item?.email) {
                  responseMap.set(item.email, {
                    success: item.success ?? true,
                    error: item.error,
                  });
                }
              });
            }
          } else {
            // For invite, response is array of objects with email and success
            const responseArray = Array.isArray(response) ? response : [];
            responseArray.forEach(
              (item: { email: string; success: boolean }) => {
                if (item?.email) {
                  responseMap.set(item.email, {
                    success: item.success ?? false,
                  });
                }
              }
            );
          }

          const failedEmailsInBatch: string[] = [];
          const unprocessedEmailsInBatch: string[] = [];

          const newEmails: EmailResult[] = batch.emails.map((emailResult) => {
            // If email is already marked as failed (e.g., "User not in tenant"), preserve it
            if (emailResult.status === 'failed' && emailResult.message) {
              failedEmailsInBatch.push(emailResult.email);
              return emailResult;
            }

            const result = responseMap.get(emailResult.email);

            if (result) {
              if (result.success) {
                const mode = this.operationMode();
                // If this is a successful removal, add to cache
                if (mode === 'remove') {
                  this.addToRemovedUsersCache(emailResult.email);
                }
                return {
                  ...emailResult,
                  status: 'succeeded',
                  message:
                    mode === 'remove'
                      ? 'Successfully removed.'
                      : 'Successfully invited.',
                };
              } else {
                // Track failed email
                failedEmailsInBatch.push(emailResult.email);
                const mode = this.operationMode();
                return {
                  ...emailResult,
                  status: 'failed',
                  message:
                    result.error ||
                    (mode === 'remove'
                      ? 'Failed to remove.'
                      : 'Failed to invite.'),
                };
              }
            }

            // Handle unprocessed emails differently for remove vs invite
            const mode = this.operationMode();
            if (mode === 'remove') {
              // For remove, if we have a response with results and email is not in successful or failed, mark as unprocessed
              if (response?.results) {
                unprocessedEmailsInBatch.push(emailResult.email);
              }
              return emailResult;
            } else {
              unprocessedEmailsInBatch.push(emailResult.email);
              return emailResult;
            }
          });

          if (failedEmailsInBatch.length > 0) {
            this.unsuccessfulEmailsList.update((current) => [
              ...current,
              ...failedEmailsInBatch,
            ]);
          }

          if (unprocessedEmailsInBatch.length > 0) {
            this.unprocessedEmailsList.update((current) => [
              ...current,
              ...unprocessedEmailsInBatch,
            ]);
          }

          const hasSucceeded = newEmails.some((e) => e.status === 'succeeded');
          const hasFailed = newEmails.some((e) => e.status === 'failed');

          let batchStatus: EmailStatus;
          if (hasSucceeded && hasFailed) {
            batchStatus = 'partial';
          } else if (hasFailed) {
            batchStatus = 'failed';
          } else {
            batchStatus = 'succeeded';
          }

          s.batches[batchIndex] = {
            ...batch,
            emails: newEmails,
            status: batchStatus,
            response,
          };
        }
        return { ...s };
      });
    } catch (error) {
      console.error('Error updating batch results:', error);
    }
  }

  /**
   * Helper to set a batch status.
   */
  private updateBatchStatus(batchId: number, status: EmailStatus): void {
    this.state.update((s) => {
      const batch = s.batches.find((b) => b.batchId === batchId);
      if (batch) {
        batch.status = status;
      }
      return { ...s };
    });
  }

  /**
   * Helper to handle a global failure and stop the process.
   */
  private handleFailure(batch: BatchResult, error: HttpErrorResponse): void {
    const errorMessage = `Batch ${batch.batchId} failed after retry with status ${error.status}. Process stopped.`;

    this.updateBatchStatus(batch.batchId, 'failed');

    this.state.update((s) => ({
      ...s,
      isProcessing: false,
      globalStatus: 'stopped',
      errorMessage: errorMessage,
    }));

    this.captureUnprocessedEmails();
    console.error(errorMessage, error);
  }

  /**
   * Captures emails from batches that were never processed (still pending or running)
   * and adds them to the unprocessed emails list
   */
  private captureUnprocessedEmails(): void {
    const batches = this.state().batches;
    const unprocessedEmails: string[] = [];

    batches.forEach((batch) => {
      if (batch.status === 'pending' || batch.status === 'running') {
        batch.emails.forEach((emailResult) => {
          unprocessedEmails.push(emailResult.email);
        });
      } else if (batch.status === 'failed' || batch.status === 'retrying') {
        batch.emails.forEach((emailResult) => {
          if (emailResult.status === 'pending') {
            unprocessedEmails.push(emailResult.email);
          }
        });
      }
    });

    if (unprocessedEmails.length > 0) {
      this.unprocessedEmailsList.update((current) => {
        const existing = new Set(current);
        unprocessedEmails.forEach((email) => {
          if (!existing.has(email)) {
            existing.add(email);
          }
        });
        return Array.from(existing);
      });
    }
  }

  private async parseCSVFileToValues(file: File): Promise<string[]> {
    try {
      const content = await file.text();
      let values: string[] = [];
      const rows = content.split(/\r?\n/);

      rows.forEach((row: string) => {
        const trimmedRow = row.trim();
        if (trimmedRow.length > 0) {
          if (trimmedRow.includes(',')) {
            const rowValues = trimmedRow
              .split(',')
              .map((value: string) => value.trim())
              .filter((value: string) => value.length > 0); // Filter out empty values from trailing commas
            values.push(...rowValues);
          } else {
            // If no commas, treat the entire row as a single value
            values.push(trimmedRow);
          }
        }
      });

      // Filter out empty values
      values = values.filter((value: string) => value.length > 0);

      return values;
    } catch (err) {
      console.error('Could not parse the CSV file to values correctly', err);

      return [];
    }
  }

  private sliceEmailsToSubmitIntoBatches(emails: Set<string>): string[][] {
    const batches = [];
    for (let i = 0; i < emails.size; i += EMAIL_BATCH_SIZE) {
      batches.push(Array.from(emails).slice(i, i + EMAIL_BATCH_SIZE));
    }

    return batches;
  }

  prepareEmailsToSubmit() {
    const emails = this.emailsToSubmit();
    const batches = this.sliceEmailsToSubmitIntoBatches(emails as Set<string>);

    this.emailsToSubmitBatches.set(batches);
  }

  private async parseValues(values: string[]): Promise<ParseValues> {
    const emailsParsed = [];
    const valuesIgnored = [];
    for (const value of values) {
      // Skip empty values - they shouldn't be counted as ignored
      const trimmedValue = value.trim();
      if (trimmedValue.length === 0) {
        continue;
      }

      if (!emailRegex.test(trimmedValue)) {
        this.logger.warn(
          `A value from the CSV failed the RegExp test: "${trimmedValue}"`
        );
        valuesIgnored.push(trimmedValue);
      } else {
        this.emailsToSubmit.update((currentSet) => {
          const newSet = new Set(currentSet);
          if (!currentSet.has(trimmedValue)) {
            newSet.add(trimmedValue);
          } else {
            this.duplicateEmails.update((current) => [
              ...current,
              trimmedValue,
            ]);
          }
          return newSet;
        });
        emailsParsed.push(trimmedValue);
      }
    }

    return {
      emailsParsed,
      valuesIgnored,
    };
  }

  async handleCSVFilesImport(fileList: FileList) {
    if (!fileList) {
      throw new Error('File list missing');
    }

    const filesResultsBucket = <FileDetail[]>[];

    if (fileList) {
      for await (const file of fileList) {
        const fileResult = {} as FileDetail;
        fileResult.errors = [];
        fileResult.name = file.name;
        fileResult.size = file.size;
        fileResult.type = file.type;
        fileResult.emailsParsed = 0;
        fileResult.valuesIgnored = 0;

        /**
         * Check 1: Check for naming conflicts
         */
        const fileNameExist = this.processedFiles().some(
          (fileDropped) => fileDropped.name === file.name
        );
        if (fileNameExist) {
          fileResult.errors.push({
            type: 'file',
            message: 'Naming conflict',
          });
          filesResultsBucket.push(fileResult);
          continue;
        }

        /**
         * Check 2: We only support .csv or .txt files.
         */
        if (!SUPPORTED_FILES.includes(file.type)) {
          fileResult.errors.push({
            type: 'file',
            message: 'Unsupported file format',
          });
          filesResultsBucket.push(fileResult);
          continue;
        }

        /**
         * Check 3: We have a max limit of size we process due to memory
         * and arbiturary time set. But in reality, this can be set differently.
         */
        if (this.totalFileSizeUploaded() + fileResult.size > MAX_ALLOWED_SIZE) {
          fileResult.errors.push({
            type: 'file',
            message: 'File too large - max allowed size reached',
          });
          filesResultsBucket.push(fileResult);
          continue;
        }

        /**
         * Check 4: Is the file correct format and not malformed?
         */
        const values = await this.parseCSVFileToValues(file);
        if (!values) {
          fileResult.errors.push({
            type: 'file',
            message: 'File is formatted wrong (syntax error)',
          });
          filesResultsBucket.push(fileResult);
          continue;
        }

        /**
         * Check individual values if they are emails, and if they follow
         * the tenants email RegExp.
         */
        const { emailsParsed, valuesIgnored } = await this.parseValues(values);
        if (
          this.totalEmailsParsed() + emailsParsed.length >
          MAX_ALLOWED_NUM_OF_EMAILS
        ) {
          fileResult.errors.push({
            type: 'file',
            message: `Too many emails to process in total`,
          });
          filesResultsBucket.push(fileResult);
          continue;
        }
        fileResult.valuesIgnored = valuesIgnored.length;
        fileResult.emailsParsed = emailsParsed.length;
        this.totalFileSizeUploaded.update(
          (current) => current + fileResult.size
        );
        this.totalEmailsParsed.update(
          (current) => current + fileResult.emailsParsed
        );
        this.totalValuesIgnored.update(
          (current) => current + fileResult.valuesIgnored
        );
        filesResultsBucket.push(fileResult);
      }

      this.processedFiles.update((state) => [...state, ...filesResultsBucket]);
    }
  }
}
